import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { LayoutDashboard, Loader2, Plus, Trash2, X } from "lucide-react";
import { VariavelWiredSelect } from "../../VariavelWiredSelect";
import {
  WIRED_HUD_FORMATOS,
  WIRED_HUD_CATEGORIAS,
  WIRED_HUD_ICONES,
  WIRED_HUD_VISOES,
  WiredConexao,
  WiredHudAcao,
  WiredHudFormato,
  WiredHudCategoria,
  WiredHudVariavel,
  WiredHudVisao,
  wiredService,
} from "../../../services/wired";

interface WiredHudVariavelModalProps {
  isOpen: boolean;
  onClose: () => void;
  variavel: WiredHudVariavel | null;
  conexoes: WiredConexao[];
  acoes: WiredHudAcao[];
  onSuccess?: () => void;
}

interface LinhaMapa {
  valor: string;
  texto: string;
}

type LimiteModo = "variavel" | "fixo";

const SEM_ICONE = "none";
const SEM_ACAO = "none";
const NOME_VARIAVEL_REGEX = /^[\w.-]{1,128}$/;
const LIMITE_FIXO_REGEX = /^[1-9]\d{0,17}$/;

export function WiredHudVariavelModal({
  isOpen,
  onClose,
  variavel,
  conexoes,
  acoes,
  onSuccess,
}: WiredHudVariavelModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [idConexao, setIdConexao] = useState("");
  const [nomeVariavel, setNomeVariavel] = useState("");
  const [rotulo, setRotulo] = useState("");
  const [icone, setIcone] = useState(SEM_ICONE);
  const [formato, setFormato] = useState<WiredHudFormato>("numero");
  const [categoria, setCategoria] = useState<WiredHudCategoria>("outros");
  const [visao, setVisao] = useState<WiredHudVisao>("pessoal");
  const [ativo, setAtivo] = useState(true);
  const [mapa, setMapa] = useState<LinhaMapa[]>([]);
  const [limiteModo, setLimiteModo] = useState<LimiteModo>("variavel");
  const [maxNome, setMaxNome] = useState("");
  const [maxFixo, setMaxFixo] = useState("");
  const [idAcao, setIdAcao] = useState(SEM_ACAO);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setIdConexao(variavel?.id_conexao ?? conexoes[0]?.id ?? "");
    setNomeVariavel(variavel?.nome_variavel ?? "");
    setRotulo(variavel?.rotulo ?? "");
    setIcone(variavel?.icone ?? SEM_ICONE);
    setFormato(variavel?.formato ?? "numero");
    setCategoria(variavel?.categoria ?? "outros");
    setVisao(variavel?.visao ?? "pessoal");
    setAtivo(variavel?.ativo ?? true);
    setMapa(
      Object.entries(variavel?.mapa_valores ?? {}).map(([valor, texto]) => ({ valor, texto }))
    );
    setLimiteModo(variavel?.max_valor_fixo ? "fixo" : "variavel");
    setMaxNome(variavel?.max_nome_variavel ?? "");
    setMaxFixo(variavel?.max_valor_fixo ?? "");
    setIdAcao(variavel?.id_acao ?? SEM_ACAO);
    setError("");
  }, [isOpen, variavel, conexoes]);

  const validar = (): string => {
    if (!idConexao) return "Selecione uma conexão.";
    if (!NOME_VARIAVEL_REGEX.test(nomeVariavel.trim())) {
      return "Nome da variável inválido (letras, números, _, - e .).";
    }
    if (!rotulo.trim() || rotulo.trim().length > 60) return "Rótulo é obrigatório (máximo 60 caracteres).";
    if (formato === "barra") {
      if (limiteModo === "variavel" && !NOME_VARIAVEL_REGEX.test(maxNome.trim())) {
        return "Informe a variável do limite máximo (letras, números, _, - e .).";
      }
      if (limiteModo === "fixo" && !LIMITE_FIXO_REGEX.test(maxFixo.trim())) {
        return "O limite fixo deve ser um inteiro maior que zero.";
      }
    }
    if (formato === "texto_por_valor") {
      const vistos = new Set<string>();
      for (const linha of mapa) {
        if (!/^-?\d{1,19}$/.test(linha.valor.trim())) return `Valor "${linha.valor}" inválido (use números inteiros).`;
        if (!linha.texto.trim() || linha.texto.trim().length > 60) return "Cada valor precisa de um texto (máximo 60 caracteres).";
        if (vistos.has(linha.valor.trim())) return `Valor ${linha.valor.trim()} repetido.`;
        vistos.add(linha.valor.trim());
      }
    }
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const mensagem = validar();
    if (mensagem) {
      setError(mensagem);
      return;
    }

    const mapaValores =
      formato === "texto_por_valor" && mapa.length > 0
        ? Object.fromEntries(mapa.map((l) => [l.valor.trim(), l.texto.trim()]))
        : null;

    const payload = {
      id_conexao: idConexao,
      nome_variavel: nomeVariavel.trim(),
      rotulo: rotulo.trim(),
      icone: icone === SEM_ICONE ? null : icone,
      formato,
      categoria,
      mapa_valores: mapaValores,
      max_nome_variavel: formato === "barra" && limiteModo === "variavel" ? maxNome.trim() : null,
      max_valor_fixo: formato === "barra" && limiteModo === "fixo" ? maxFixo.trim() : null,
      id_acao: idAcao === SEM_ACAO ? null : idAcao,
      visao,
      ativo,
    };

    try {
      setSubmitting(true);
      if (variavel) {
        await wiredService.atualizarHudVariavel(variavel.id, payload);
        enqueueSnackbar("Variável do HUD atualizada com sucesso!", { variant: "success" });
      } else {
        await wiredService.criarHudVariavel(payload);
        enqueueSnackbar("Variável do HUD criada com sucesso!", { variant: "success" });
      }
      onClose();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || "Erro ao salvar variável do HUD", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const ajudaFormato = WIRED_HUD_FORMATOS.find((f) => f.value === formato)?.ajuda;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <LayoutDashboard className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {variavel ? "Editar variável do HUD" : "Nova variável do HUD"}
            </CardTitle>
            <CardDescription>Escolha o que aparece na janela flutuante do HUD</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0" disabled={submitting}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Conexão</Label>
              <Select value={idConexao} onValueChange={setIdConexao} disabled={submitting}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma conexão" />
                </SelectTrigger>
                <SelectContent>
                  {conexoes.map((conexao) => (
                    <SelectItem key={conexao.id} value={conexao.id}>
                      {conexao.nome} (quarto {conexao.room_id}){!conexao.ativo ? " - inativa" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hud_nome_variavel">Nome da variável WIRED</Label>
              <VariavelWiredSelect
                id="hud_nome_variavel"
                value={nomeVariavel}
                onChange={(v) => {
                  setNomeVariavel(v);
                  setError("");
                }}
                idConexao={idConexao}
                ativo={isOpen}
                placeholder="Buscar variável (ex: horas_servico)"
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label>Categoria no HUD</Label>
              <Select value={categoria} onValueChange={(v) => setCategoria(v as WiredHudCategoria)} disabled={submitting}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WIRED_HUD_CATEGORIAS.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Define em qual módulo a variável aparece no HUD tático.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hud_rotulo">Rótulo</Label>
                <Input
                  id="hud_rotulo"
                  value={rotulo}
                  onChange={(e) => {
                    setRotulo(e.target.value);
                    setError("");
                  }}
                  placeholder="Ex: Horas de serviço"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label>Ícone</Label>
                <Select value={icone} onValueChange={setIcone} disabled={submitting}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sem ícone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SEM_ICONE}>Sem ícone</SelectItem>
                    {WIRED_HUD_ICONES.map((nome) => (
                      <SelectItem key={nome} value={nome}>
                        {nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Formato</Label>
                <Select value={formato} onValueChange={(v) => setFormato(v as WiredHudFormato)} disabled={submitting}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WIRED_HUD_FORMATOS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {ajudaFormato && <p className="text-xs text-muted-foreground">{ajudaFormato}</p>}
              </div>
              <div className="space-y-2">
                <Label>Quem vê</Label>
                <Select value={visao} onValueChange={(v) => setVisao(v as WiredHudVisao)} disabled={submitting}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WIRED_HUD_VISOES.map((v) => (
                      <SelectItem key={v.value} value={v.value}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formato === "barra" && (
              <div className="space-y-3 rounded-lg border border-border p-3">
                <div>
                  <Label>Limite máximo da barra</Label>
                  <p className="text-xs text-muted-foreground">
                    A variável acima é o valor atual. O limite pode vir de outra variável do jogo ou ser um número fixo.
                  </p>
                </div>
                <div className="flex gap-2">
                  {(["variavel", "fixo"] as LimiteModo[]).map((modo) => (
                    <Button
                      key={modo}
                      type="button"
                      size="sm"
                      variant={limiteModo === modo ? "default" : "outline"}
                      onClick={() => {
                        setLimiteModo(modo);
                        setError("");
                      }}
                      disabled={submitting}
                    >
                      {modo === "variavel" ? "Outra variável" : "Valor fixo"}
                    </Button>
                  ))}
                </div>
                {limiteModo === "variavel" ? (
                  <Input
                    value={maxNome}
                    onChange={(e) => {
                      setMaxNome(e.target.value);
                      setError("");
                    }}
                    placeholder="Ex: item_kit_max"
                    className="font-mono"
                    disabled={submitting}
                  />
                ) : (
                  <Input
                    value={maxFixo}
                    onChange={(e) => {
                      setMaxFixo(e.target.value);
                      setError("");
                    }}
                    placeholder="Ex: 5"
                    inputMode="numeric"
                    disabled={submitting}
                  />
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Botão de ação (opcional)</Label>
              <Select value={idAcao} onValueChange={setIdAcao} disabled={submitting}>
                <SelectTrigger>
                  <SelectValue placeholder="Sem botão" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_ACAO}>Sem botão</SelectItem>
                  {acoes.map((acao) => (
                    <SelectItem key={acao.id} value={acao.id}>
                      {acao.rotulo} ({acao.nome_variavel_disparo} = {acao.valor_disparo})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Mostra um botão neste cartão do HUD (ex.: Usar). Cadastre a ação em "Ações do HUD".
              </p>
            </div>

            {formato === "texto_por_valor" && (
              <div className="space-y-2 rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <Label>Texto de cada valor</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setMapa([...mapa, { valor: "", texto: "" }])}
                    disabled={submitting}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                {mapa.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Sem itens, o HUD mostra o número do valor.
                  </p>
                )}
                {mapa.map((linha, indice) => (
                  <div key={indice} className="flex items-center gap-2">
                    <Input
                      value={linha.valor}
                      onChange={(e) =>
                        setMapa(mapa.map((l, i) => (i === indice ? { ...l, valor: e.target.value } : l)))
                      }
                      placeholder="Valor"
                      className="w-28 font-mono"
                      disabled={submitting}
                    />
                    <Input
                      value={linha.texto}
                      onChange={(e) =>
                        setMapa(mapa.map((l, i) => (i === indice ? { ...l, texto: e.target.value } : l)))
                      }
                      placeholder="Texto exibido"
                      disabled={submitting}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setMapa(mapa.filter((_, i) => i !== indice))}
                      className="h-8 w-8 p-0 text-red-600"
                      disabled={submitting}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label htmlFor="hud_ativo">Ativo</Label>
              <Switch id="hud_ativo" checked={ativo} onCheckedChange={setAtivo} disabled={submitting} />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : variavel ? (
                  "Atualizar"
                ) : (
                  "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
